"use client";

import type { AuthAuditEvent, ExternalToken } from "@webuild-ai/react-auth";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { logger } from "@/telemetry";
import { isAllowedParentOrigin } from "@/config/allowedParentOrigins";

/**
 * Embedded (iframe) SSO for hosting Pathway inside a trusted parent application such as
 * SharePoint. See docs/SHAREPOINT_IFRAME_SSO.md and react-auth ADR-0008.
 *
 * This provider runs the postMessage handshake with the parent window and feeds the
 * result into `@webuild-ai/react-auth` (via AuthUserProvider's `embedded` / `loginHint` /
 * `externalToken` / `onInteractionRequired` / `onAuthEvent` props). Because react-auth's
 * `useAuth()` then reports authenticated from the injected token, every existing consumer
 * (the API clients, the SSE client, `authenticatedFetch`, route guards) keeps working
 * unchanged.
 *
 * Two-tier authentication:
 *   tier 1 - the parent sends the user's email (USER_INFO); react-auth attempts MSAL
 *            ssoSilent with it. Azure AD remains the authority.
 *   tier 2 - the parent sends a pre-acquired access token (AUTH_TOKEN); used as a
 *            fallback when ssoSilent cannot complete (third-party cookies blocked).
 *
 * SECURITY: every inbound message is validated against an origin allow-list
 * (`isAllowedParentOrigin`) and `event.source`. The token is held in memory only and is
 * never persisted or logged. Outbound control messages (AUTH_REQUEST, REQUEST_LOGOUT)
 * carry no secret, so they use a "*" target origin; only inbound messages are trusted.
 */

/** Milliseconds to wait for the parent to respond before declaring failure. */
const PARENT_AUTH_TIMEOUT_MS = 8000;

/** Fallback token lifetime if the parent omits `expiresAt`. */
const DEFAULT_TOKEN_TTL_MS = 60 * 60 * 1000;

export type EmbeddedAuthStatus = "idle" | "resolving" | "ready" | "failed" | "logged-out";

/*
 * Inbound message shapes (parent to child). Only `type` is checked before a
 * handler runs, so every payload field is typed `unknown` and the compiler makes
 * each handler narrow a field before it can use it.
 */

/** Inbound AUTH_TOKEN message shape. */
interface AuthTokenMessage {
  type: "AUTH_TOKEN";
  accessToken?: unknown;
  expiresAt?: unknown;
  user?: { sub?: unknown; id?: unknown; name?: unknown; email?: unknown };
}

/** Inbound AUTH_REFRESH message shape. */
interface AuthRefreshMessage {
  type: "AUTH_REFRESH";
  accessToken?: unknown;
  expiresAt?: unknown;
}

/** Inbound USER_INFO message shape, which supplies the ssoSilent loginHint. */
interface UserInfoMessage {
  type: "USER_INFO";
  user?: { name?: unknown; email?: unknown; initials?: unknown };
}

/** A payload field as a string, or undefined when it is anything else. */
function readString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** The trimmed email a message's user carries, or undefined when it has none. */
function readEmailHint(user: { email?: unknown } | undefined): string | undefined {
  return readString(user?.email)?.trim();
}

/** Every inbound message this provider acts on, discriminated by `type`. */
type InboundAuthMessage =
  AuthTokenMessage | AuthRefreshMessage | UserInfoMessage | { type: "AUTH_LOGOUT" };

const INBOUND_AUTH_MESSAGE_TYPES: ReadonlySet<unknown> = new Set<InboundAuthMessage["type"]>([
  "AUTH_TOKEN",
  "AUTH_REFRESH",
  "USER_INFO",
  "AUTH_LOGOUT",
]);

/**
 * Whether parent-window data carries one of the message types this provider
 * acts on. Only the discriminant is checked here. The payload is still
 * untrusted, which is why the message shapes type every other field `unknown`,
 * so no handler can read one without narrowing it first.
 */
function isInboundAuthMessage(data: unknown): data is InboundAuthMessage {
  if (data === null || typeof data !== "object") return false;

  return INBOUND_AUTH_MESSAGE_TYPES.has(Reflect.get(data, "type"));
}

export interface EmbeddedAuthConfig {
  /** True when Pathway is running inside an iframe. */
  isEmbedded: boolean;
  /** Handshake status (idle when not embedded). */
  status: EmbeddedAuthStatus;
  /** Email hint for react-auth ssoSilent (tier 1). */
  loginHint?: string;
  /** Injected access token for react-auth (tier 2). */
  externalToken: ExternalToken | null;
  /** Set when the handshake failed (e.g. parent never responded). */
  error: string | null;
  /** Ask the parent to (re)send authentication. */
  requestAuth: () => void;
  /** Passed to react-auth: called when it needs a fresh token. */
  onInteractionRequired: () => void;
  /** Passed to react-auth: receives the PII-free audit trail. */
  onAuthEvent: (event: AuthAuditEvent) => void;
}

const NOOP = () => {};

const EmbeddedAuthContext = createContext<EmbeddedAuthConfig>({
  isEmbedded: false,
  status: "idle",
  externalToken: null,
  error: null,
  requestAuth: NOOP,
  onInteractionRequired: NOOP,
  onAuthEvent: NOOP,
});

/** Access the embedded auth configuration. Returns inert defaults when not embedded. */
export const useEmbeddedAuthConfig = (): EmbeddedAuthConfig => useContext(EmbeddedAuthContext);

/** Detect whether the app is running inside an iframe. */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent throws on access, which itself means we are embedded.
    return true;
  }
}

/** Whether a token message carries a non-blank access token. */
function hasAccessToken(message: { accessToken?: unknown }): message is { accessToken: string } {
  return typeof message.accessToken === "string" && message.accessToken.trim() !== "";
}

/**
 * The user an AUTH_TOKEN names: undefined when it names none, or null when it
 * names one without a usable id, which rejects the message.
 */
function readTokenUser(message: AuthTokenMessage): ExternalToken["user"] | null {
  if (!message.user) return undefined;

  const id = String(message.user.sub ?? message.user.id ?? "").trim();

  if (!id) return null;

  return { id, name: readString(message.user.name), email: readString(message.user.email) };
}

export const EmbeddedAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const embedded = useMemo(() => isInIframe(), []);
  const [externalToken, setExternalToken] = useState<ExternalToken | null>(null);
  const [loginHint, setLoginHint] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<EmbeddedAuthStatus>(embedded ? "resolving" : "idle");
  const [error, setError] = useState<string | null>(null);
  // True once the parent has responded at all (token or hint).
  const receivedRef = useRef(false);

  const postToParent = useCallback((message: Record<string, unknown>) => {
    // Control messages carry no secret. We do not know the validated parent origin until
    // an inbound message arrives, so "*" is acceptable here; all INBOUND messages are
    // origin-validated before use.
    try {
      window.parent?.postMessage(message, "*");
    } catch {
      logger.warn("[embedded-auth] failed to post message to parent");
    }
  }, []);

  const requestAuth = useCallback(() => {
    if (!embedded) return;
    postToParent({ type: "AUTH_REQUEST" });
  }, [embedded, postToParent]);

  const onInteractionRequired = useCallback(() => {
    if (!embedded) return;
    logger.info("[embedded-auth] interaction required - requesting token from parent");
    setStatus((current) => (current === "ready" ? current : "resolving"));
    requestAuth();
  }, [embedded, requestAuth]);

  const onAuthEvent = useCallback(
    (event: AuthAuditEvent) => {
      // Audit events are PII-free and token-free by contract; forward to telemetry.
      logger.info("[embedded-auth][audit]", {
        type: event.type,
        mode: event.mode ?? "",
        userId: event.userId ?? "",
        ...(event.detail ?? {}),
      });

      // react-auth signals logout in embedded mode by emitting this event. Ask the parent
      // to perform the actual sign-out; never navigate the IdP inside the iframe.
      if (event.type === "embedded_logout") {
        postToParent({ type: "REQUEST_LOGOUT" });
      }
    },
    [postToParent],
  );

  useEffect(() => {
    if (!embedded) return;

    const handleAuthToken = (message: AuthTokenMessage) => {
      if (!hasAccessToken(message)) return;

      const user = readTokenUser(message);

      if (user === null) {
        logger.warn("[embedded-auth] rejected AUTH_TOKEN with no user id");

        return;
      }

      receivedRef.current = true;
      setExternalToken({
        accessToken: message.accessToken,
        expiresAt:
          typeof message.expiresAt === "number"
            ? message.expiresAt
            : Date.now() + DEFAULT_TOKEN_TTL_MS,
        user,
      });
      const tokenHint = readEmailHint(message.user);

      if (tokenHint) {
        setLoginHint(tokenHint);
      }
      setError(null);
      setStatus("ready");
    };

    const handleAuthRefresh = (message: AuthRefreshMessage) => {
      if (!hasAccessToken(message)) return;

      receivedRef.current = true;
      // Construct a token even if none was held yet (e.g. refresh arrives first),
      // so a late refresh can always recover a usable session.
      setExternalToken((previous) => ({
        accessToken: message.accessToken,
        expiresAt:
          typeof message.expiresAt === "number"
            ? message.expiresAt
            : (previous?.expiresAt ?? Date.now() + DEFAULT_TOKEN_TTL_MS),
        user: previous?.user,
      }));
      setError(null);
      setStatus("ready");
    };

    const handleUserInfo = (message: UserInfoMessage) => {
      const hint = readEmailHint(message.user);

      // A USER_INFO without a usable email hint is a no-op: receivedRef stays false
      // so the initial-handshake timeout still fires if no token/hint ever arrives.
      if (hint) {
        receivedRef.current = true;
        // Tier 1: hand the hint to react-auth so it can attempt ssoSilent.
        setLoginHint(hint);
      }
    };

    const handleAuthLogout = () => {
      // Mark as received so the initial-handshake timeout does not also fire.
      receivedRef.current = true;
      setExternalToken(null);
      setLoginHint(undefined);
      setError(null);
      setStatus("logged-out");
    };

    // THEME_CHANGE / LANGUAGE_CHANGE etc. are not auth concerns, so have no handler
    // here. Switching on the discriminant narrows each message without a cast.
    const dispatchAuthMessage = (message: InboundAuthMessage) => {
      switch (message.type) {
        case "AUTH_TOKEN":
          return handleAuthToken(message);
        case "AUTH_REFRESH":
          return handleAuthRefresh(message);
        case "USER_INFO":
          return handleUserInfo(message);
        case "AUTH_LOGOUT":
          return handleAuthLogout();
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) {
        logger.warn("[embedded-auth] rejected message from unauthorised origin", {
          origin: event.origin,
        });

        return;
      }

      // Only accept messages from our actual parent window. A null source (synthetic
      // events, sandboxed frames without allow-same-origin) is rejected too.
      if (event.source !== window.parent) {
        return;
      }

      if (isInboundAuthMessage(event.data)) {
        dispatchAuthMessage(event.data);
      }
    };

    window.addEventListener("message", handleMessage);
    requestAuth();

    const timeout = window.setTimeout(() => {
      if (!receivedRef.current) {
        setStatus("failed");
        setError("Timed out waiting for authentication from the parent application.");
        logger.warn("[embedded-auth] timed out waiting for parent authentication");
      }
    }, PARENT_AUTH_TIMEOUT_MS);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(timeout);
    };
  }, [embedded, requestAuth]);

  const value = useMemo<EmbeddedAuthConfig>(
    () => ({
      isEmbedded: embedded,
      status,
      loginHint,
      externalToken,
      error,
      requestAuth,
      onInteractionRequired,
      onAuthEvent,
    }),
    [
      embedded,
      status,
      loginHint,
      externalToken,
      error,
      requestAuth,
      onInteractionRequired,
      onAuthEvent,
    ],
  );

  return <EmbeddedAuthContext.Provider value={value}>{children}</EmbeddedAuthContext.Provider>;
};
